// Outbreak dots: per-ZIP report dots baked as a single sprite drawn on one GPU symbol
// layer (icon-image + icon-size), so rendering scales without any DOM markers. Every dot
// is solid red — it marks where symptom reports came from — and every dot draws at the
// smallest size unless its ZIP stands well clear of the typical count on screen.
import {
  MAP_LAB_PIE_BASE_DIAMETER,
  MAP_LAB_PIE_FLOOR_DIAMETER,
  MAP_LAB_PIE_SICK_COLOR,
  MAP_LAB_PIE_SIZE_STEPS,
} from "./constants.js";

// Images are drawn at 2x device pixels so they stay crisp when Mapbox scales them.
const PIE_PIXEL_RATIO = 2;

// White ring around each dot, in on-screen pixels.
const PIE_STROKE = 1.5;

// The one baked dot sprite every People marker uses.
export function sickDotImageId() {
  return "sick-dot";
}

// The count a dot is sized by: symptom REPORTS in that ZIP (healthy reports never count,
// even with no symptom filter applied). Deliberately not people_sick_count — a report
// carries one symptom list for everyone it covers, so under a symptom filter the people
// total would sweep in people the filter can't actually attribute that symptom to, while
// a report either names the symptom or it doesn't. Counts arrive already narrowed to the
// active filter (see recomputeMapLabDerived), so the dot always reflects what is filtered.
// Returns 0 for a ZIP that draws no dot at all, so callers build their comparison pool
// from the same rule outbreakPieFeature drops rows by.
export function pieDotCount(properties = {}) {
  const total = nonNegativeNumber(properties.total_count);
  const sick = nonNegativeNumber(properties.sick_count);
  if (total <= 0 || sick <= 0) return 0;
  return sick;
}

// The count every other ZIP is measured against: the median of the drawn counts. Median
// rather than mean so the handful of runaway ZIPs a real outbreak map always has cannot
// drag the bar up and mute everything else. sortedCounts arrives ascending, so this is a
// lookup rather than a scan. An even-length pool takes the upper of the two middles, which
// biases the bar high and the map quiet — the direction to err in. Returns 0 for an empty
// pool, which callers read as "no basis for comparison".
export function typicalCount(sortedCounts) {
  const counts = Array.isArray(sortedCounts) ? sortedCounts : [];
  if (!counts.length) return 0;
  return counts[Math.floor(counts.length / 2)];
}

// A ZIP's dot diameter in on-screen pixels: the first step it clears on BOTH tests wins,
// and a ZIP clearing none draws the floor. sortedCounts is every drawn ZIP's count in
// ascending order, including this one. A lone dot draws the FLOOR, and so does every dot
// when the pool is missing entirely — with nothing to compare against there is no evidence
// any ZIP is remarkable, so they all stay quiet.
export function dotDiameter(count, sortedCounts) {
  const value = nonNegativeNumber(count);
  const typical = typicalCount(sortedCounts);
  if (typical <= 0) return MAP_LAB_PIE_FLOOR_DIAMETER;
  const step = MAP_LAB_PIE_SIZE_STEPS.find(
    (candidate) => value >= typical * candidate.multiple && value >= candidate.minCount
  );
  return step ? step.diameter : MAP_LAB_PIE_FLOOR_DIAMETER;
}

// Mapbox icon-size for that diameter. The sprite is baked at the largest step, so every
// step other than the top scales the image down.
export function dotScale(count, sortedCounts) {
  return dotDiameter(count, sortedCounts) / MAP_LAB_PIE_BASE_DIAMETER;
}

// Convert one ZIP centroid with aggregated People counts into a display feature. A ZIP
// with no symptom reports emits NO marker at all, rather than drawing a dot the legend
// can't explain. Returns a single feature or null; callers drop the nulls.
export function outbreakPieFeature(feature, options = {}) {
  const properties = feature?.properties || {};
  const total = nonNegativeNumber(properties.total_count);
  const sick = pieDotCount(properties);
  if (sick <= 0) return null;

  const pieScale = dotScale(sick, options.sortedCounts);

  return {
    ...feature,
    properties: {
      ...properties,
      kind: options.kind || "pie",
      category: "people",
      zip_code: properties.zip_code || "",
      total_count: total,
      sick_count: sick,
      // Not drawn on the dot — carried through for the ZIP popup's secondary line.
      healthy_count: nonNegativeNumber(properties.healthy_count),
      people_sick_count: nonNegativeNumber(properties.people_sick_count),
      sick_rate: sick / total,
      pieScale,
      pieIcon: sickDotImageId(),
    },
  };
}

// Register the dot sprite on this map (idempotent): a solid red circle ringed in white.
export function ensurePieImages(map) {
  const size = Math.round((MAP_LAB_PIE_BASE_DIAMETER + PIE_STROKE * 2) * PIE_PIXEL_RATIO);
  const id = sickDotImageId();
  if (map.hasImage(id)) return;
  const image = solidDotImageData(size, MAP_LAB_PIE_SICK_COLOR);
  if (image) map.addImage(id, image, { pixelRatio: PIE_PIXEL_RATIO });
}

function solidDotImageData(size, color) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) return null;
  const center = size / 2;
  const radius = center - PIE_STROKE * PIE_PIXEL_RATIO;
  context.beginPath();
  context.arc(center, center, radius, 0, Math.PI * 2);
  context.fillStyle = color;
  context.fill();
  context.beginPath();
  context.arc(center, center, radius, 0, Math.PI * 2);
  context.lineWidth = PIE_STROKE * PIE_PIXEL_RATIO;
  context.strokeStyle = "#ffffff";
  context.stroke();
  return context.getImageData(0, 0, size, size);
}

function nonNegativeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
}
